import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button } from 'react-bootstrap';
import '../assets/css/psearch.css';
import apiClient from '../config/config';
import { useCurrency } from '../context/CurrencyContext';

function LandingPageTrending({ query, onTryOn, user, savedProducts, toggleSaveProduct, setShowSignInModal, limit = null }) {
  const navigate = useNavigate();
  const { currency, getCurrencySymbol } = useCurrency();
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const normalizeProductId = (productId) => {
    if (productId === null || productId === undefined) return '';
    return String(productId);
  };

  // Helper function to properly capitalize product titles (title case)
  const capitalizeTitle = (title) => {
    if (!title) return '';
    
    // Words that should remain lowercase in title case
    const lowerCaseWords = ['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'from', 'by', 'with', 'as'];
    
    return title
      .toLowerCase()
      .split(' ')
      .map((word, index) => {
        // Always capitalize first word and words not in the lowerCaseWords list
        if (index === 0 || !lowerCaseWords.includes(word)) {
          return word.charAt(0).toUpperCase() + word.slice(1);
        }
        return word; // Keep lowercase words lowercase
      })
      .join(' ');
  };

  useEffect(() => {
    const fetchTrendingProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.get('/products/most-liked/', {
          params: { currency, limit: 10 },
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (response.data) {
          const products = response.data;
          setTrendingProducts(limit ? products.slice(0, limit) : products);
        }
      } catch (err) {
        console.error('Detailed error:', err);
        setError('Failed to fetch trending products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTrendingProducts();
  }, [currency]);

  return (
    <div style={{ marginBottom: '60px' }}>
      {loading ? (
        <div className="text-center">Loading trending products...</div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'auto',
            gap: '16px',
            paddingBottom: '12px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
          className="horizontal-scroll-row"
        >
          {trendingProducts.map((product, index) => (
            <div
              key={index}
              style={{
                flex: '0 0 260px',
                cursor: 'pointer',
              }}
              onClick={() => navigate(`/product/${product.id || encodeURIComponent(product.link)}`)}
            >
              {/* Image */}
              <div style={{
                width: '100%',
                height: '380px',
                overflow: 'hidden',
                borderRadius: '4px',
                backgroundColor: '#f5f5f5',
                position: 'relative',
              }}>
                {product.image && (
                  <img
                    src={product.image}
                    alt={product.title || 'Product Image'}
                    loading="lazy"
                    decoding="async"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center top',
                      transition: 'transform 0.3s ease',
                      filter: product.is_out_of_stock ? 'grayscale(40%)' : 'none',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.03)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = '/default-image.avif';
                    }}
                  />
                )}

                {/* Out of stock overlay */}
                {product.is_out_of_stock && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.32)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}>
                    <span style={{
                      background: '#fff',
                      color: '#1a1a1a',
                      fontSize: '10px',
                      fontWeight: '700',
                      letterSpacing: '2px',
                      textTransform: 'uppercase',
                      padding: '6px 14px',
                      fontFamily: 'Montserrat, sans-serif',
                    }}>
                      Out of Stock
                    </span>
                  </div>
                )}

                {/* Heart Save Icon */}
                <button
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'rgba(255,255,255,0.95)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 10,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s',
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!user) {
                      setShowSignInModal(true);
                      return;
                    }
                    toggleSaveProduct(product.id ?? product.link);
                  }}
                  title={savedProducts && savedProducts.includes(normalizeProductId(product.id ?? product.link)) ? 'Remove from saved' : 'Save product'}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill={savedProducts && savedProducts.includes(normalizeProductId(product.id ?? product.link)) ? '#e63946' : 'none'}
                    stroke="#e63946"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 21s-7-4.35-7-10A5 5 0 0 1 12 6a5 5 0 0 1 7 5c0 5.65-7 10-7 10z" />
                  </svg>
                </button>

                {/* Try it on Button */}
                <button
                  style={{
                    position: 'absolute',
                    bottom: '12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '8px 14px',
                    borderRadius: '999px',
                    border: 'none',
                    background: 'rgba(0,0,0,0.7)',
                    color: '#fff',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    backdropFilter: 'blur(6px)',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={(e) => { e.stopPropagation(); onTryOn(product.image); }}
                >
                  Try it on
                </button>
              </div>

              {/* Product Info */}
              <div style={{ paddingTop: '12px' }}>
                <h3 style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  fontFamily: 'Montserrat, sans-serif',
                  color: '#1a1a1a',
                  margin: '0 0 3px 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {product.brand || 'Unknown Brand'}
                </h3>
                <p style={{
                  fontSize: '12px',
                  fontFamily: 'Montserrat, sans-serif',
                  color: '#888',
                  margin: '0 0 2px 0',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {capitalizeTitle(product.title) || 'Unnamed Product'}
                </p>
                <p style={{
                  fontSize: '12px',
                  fontFamily: 'Montserrat, sans-serif',
                  color: '#888',
                  margin: 0,
                }}>
                  {getCurrencySymbol()} {product.price || 'Not Available'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{`
        .horizontal-scroll-row::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}

export default LandingPageTrending;
